import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';
import './auth/LoginDesign.css';

const ResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('token') || '';
  }, [location.search]);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('');

    if (!token) {
      setError('This reset link is missing its token. Request a new link from the login page.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword(token, password);
      setStatus('Your password has been reset. Redirecting to login…');
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        'This reset link is invalid or has expired. Request a new one from the login page.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <header className="login-header">
        <div className="login-header-inner">
          <Link to="/" className="login-logo">
            Studio<span className="login-logo-accent">AEye</span>
          </Link>
          <Link to="/login" className="login-back-link">
            ← Back to login
          </Link>
        </div>
      </header>

      <main className="login-main">
        <div className="login-card">
          <div className="login-heading">
            <h1 className="login-title">Choose a new password</h1>
            <p className="login-subtitle">
              Pick a password you have not used on this account before. Minimum 8 characters.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="login-form" noValidate>
            <div className="login-field">
              <label htmlFor="password" className="login-label">
                New password
              </label>
              <div className="login-password-wrapper">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="login-input"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="login-password-toggle"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="confirmPassword" className="login-label">
                Confirm new password
              </label>
              <input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="login-input"
                disabled={loading}
              />
            </div>

            {error && (
              <div role="alert" className="login-alert login-alert-error">
                {error}
              </div>
            )}

            {status && <div className="login-alert login-alert-success">{status}</div>}

            <button type="submit" disabled={loading} className="login-submit">
              {loading ? (
                <>
                  <span className="login-spinner" />
                  Saving new password…
                </>
              ) : (
                'Save new password'
              )}
            </button>
          </form>

          <p className="login-footer">© StudioAEye {new Date().getFullYear()}</p>
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
